import { cn } from "@/lib/utils";
import { X } from "@/components/icons";

export interface ChipItem {
  id: string;
  label: string;
}

interface ChipListProps {
  /** 배열 순서대로 노출된다 (선택 순서 유지) */
  items: ChipItem[];
  onRemove: (id: string) => void;
  emptyText?: string;
  className?: string;
}

/**
 * `[대영빌딩 ×] [영린빌딩 ×]` 형태의 제거 가능한 칩 목록.
 *
 * 칩 높이가 22px 라 × 히트 영역을 넓히면 줄 높이가 밀린다 — 아이콘 크기 그대로 둔다.
 *
 * @example
 * ```tsx
 * <ChipList
 *   items={selectedStations.map((s) => ({ id: s.uuid, label: s.name }))}
 *   onRemove={handleUnselectStation}
 *   emptyText="선택한 충전소가 없어요."
 * />
 * ```
 */
export function ChipList({
  items,
  onRemove,
  emptyText,
  className,
}: ChipListProps) {
  if (items.length === 0) {
    return emptyText ? (
      <p
        className={cn("typo-caption-1-medium text-labels-assistive", className)}
      >
        {emptyText}
      </p>
    ) : null;
  }

  return (
    <ul className={cn("flex flex-wrap gap-2", className)}>
      {items.map((item) => (
        // Badge(md) 규격 + status-available 색 (시안)
        <li
          key={item.id}
          className="inline-flex items-center gap-1 rounded-xs bg-status-available/10 px-1.5 py-[3px] inset-border-double"
        >
          <span className="typo-caption-1-semibold text-status-available">
            {item.label}
          </span>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`‘${item.label}’ 선택 해제`}
            className="flex size-3.5 shrink-0 cursor-pointer items-center justify-center"
          >
            <X size={14} color="status-available" />
          </button>
        </li>
      ))}
    </ul>
  );
}

ChipList.displayName = "ChipList";
